import React, {useMemo} from 'react';

import {FeaturesEnum} from 'presentation/constants';
import {getExamStepPosition} from 'presentation/modules/booking/exam/utils/getExamStepPosition';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {Location} from 'presentation/modules/booking/shared/ui/pages/Location';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {ExamDistrictSelection} from '../ExamDistrictSelection';
import {ExamProfessionalSelection} from '../ExamProfessionalSelection';
import {ExamSelection} from '../ExamSelection';
import {ExamUnits} from '../ExamUnits';
import {ExamBookingStepsProps} from './types';
import {ExamReviewBooking} from '../ExamReviewBooking';
import {ExamReviewBookingNew} from '../ExamReviewBookingNew';

export function ExamStepsWrapper({
	activeStep,
	handleBack,
	handleNext,
	stepLabels,
}: ExamBookingStepsProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {isManualLocation} = bookingData;
	const FLOW_TYPE = FlowEnum.EXAM;
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const stepPositions = useMemo(
		() => getExamStepPosition({isManualLocation}),
		[isManualLocation],
	);

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
	} = stepPositions;

	const stepContent = useMemo(() => {
		const baseSteps: Record<number, JSX.Element | null> = {
			[LOCATION_STEP]: <Location flowType={FLOW_TYPE} />,
			[SPECIALITY_OR_EXAM_STEP]: <ExamSelection handleNextStep={handleNext} />,
			[UNIT_STEP]: <ExamUnits handleNextStep={handleNext} />,
			[PROFESSIONAL_STEP]: <ExamProfessionalSelection handleNextStep={handleNext} />,
			[REVIEW_STEP]: isNewResumePageEnabled ? (
				<ExamReviewBookingNew handlePrevStep={handleBack} />
			) : (
				<ExamReviewBooking stepLabels={stepLabels} handlePrevStep={handleBack} />
			),
		};

		if (isManualLocation) {
			baseSteps[DISTRICT_STEP] = <ExamDistrictSelection handleNextStep={handleNext} />;
		}

		return baseSteps;
	}, [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		isManualLocation,
		isNewResumePageEnabled,
		handleNext,
		handleBack,
		activeStep,
		stepLabels,
	]);

	return stepContent[activeStep] ?? null;
}
